// Serviço para gerenciamento centralizado dos logos
import publicApi from './publicApi';

export type LogoArea = 'header' | 'sidebar' | 'login' | 'proposta' | 'footer';

export interface LogoAreaConfig {
  header: 'principal' | 'secundario';
  sidebar: 'principal' | 'secundario';
  login: 'principal' | 'secundario';
  proposta: 'principal' | 'secundario';
  footer: 'principal' | 'secundario';
}

export interface LogoConfig {
  principal: string;
  secundario: string;
  config: LogoAreaConfig;
  lastUpdated: string;
  version: number;
}

const defaultAreaConfig: LogoAreaConfig = {
  header: 'principal',
  sidebar: 'principal',
  login: 'secundario',
  proposta: 'principal',
  footer: 'secundario',
};

class LogoService {
  private static instance: LogoService;
  private storageKey = 'portal_logo_config';
  private defaultConfig: LogoConfig = {
    principal: '/logo.png',
    secundario: '/logo.png',
    config: defaultAreaConfig,
    lastUpdated: new Date().toISOString(),
    version: 1
  };
  private initialized = false;

  private constructor() { }

  static getInstance(): LogoService {
    if (!LogoService.instance) {
      LogoService.instance = new LogoService();
    }
    return LogoService.instance;
  }

  // Inicializar carregando do backend
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      const response = await publicApi.get('/theme');
      if (response.data) {
        const previousConfig = this.getLogoConfig();
        const config: LogoConfig = {
          ...this.defaultConfig,
          lastUpdated: new Date().toISOString(),
          version: previousConfig.version + 1,
        };

        // PRIORIDADE 1: Novos campos (logoFolioClaro/Escuro) - Sistema moderno
        if (response.data.logoFolioClaro || response.data.logoFolioEscuro) {
          // Detectar tema atual do documento
          const isDark = document.documentElement.classList.contains('dark');
          
          // Usar logo correspondente ao tema atual
          if (isDark && response.data.logoFolioEscuro) {
            config.principal = response.data.logoFolioEscuro;
            config.secundario = response.data.logoFolioClaro || response.data.logoFolioEscuro;
          } else if (response.data.logoFolioClaro) {
            config.principal = response.data.logoFolioClaro;
            config.secundario = response.data.logoFolioEscuro || response.data.logoFolioClaro;
          }
          
          console.log('[LOGO] Usando logos modernas (Folio Claro/Escuro)');
        } 
        // FALLBACK: Campos antigos (logo/logoSecundario) - Compatibilidade
        else {
          if (response.data.logo) {
            config.principal = response.data.logo;
          }
          if (response.data.logoSecundario) {
            config.secundario = response.data.logoSecundario;
          }
          console.log('[LOGO] Usando logos antigas (compatibilidade)');
        }

        // Configuração de áreas (qual logo usar em cada lugar)
        if (response.data.logoConfig) {
          try {
            config.config = JSON.parse(response.data.logoConfig);
          } catch {
            config.config = defaultAreaConfig;
          }
        }

        this.saveLogoConfig(config);
      }
      this.initialized = true;
    } catch (error) {
      console.log('[LOGO] Usando logo do cache local');
      this.initialized = true;
    }
  }

  // Obter configuração atual
  getLogoConfig(): LogoConfig {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração da logo:', error);
    }
    return this.defaultConfig;
  }

  // Salvar configuração
  private saveLogoConfig(config: LogoConfig): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(config));
      this.notifyLogoChange(config);
    } catch (error) {
      console.error('Erro ao salvar configuração da logo:', error);
    }
  }

  // Atualizar ambos os logos
  async updateLogos(data: { principal?: string; secundario?: string; config?: LogoAreaConfig }): Promise<void> {
    const currentConfig = this.getLogoConfig();

    const newConfig: LogoConfig = {
      principal: data.principal || currentConfig.principal,
      secundario: data.secundario || currentConfig.secundario,
      config: data.config || currentConfig.config,
      lastUpdated: new Date().toISOString(),
      version: currentConfig.version + 1
    };

    this.saveLogoConfig(newConfig);
    this.updateFavicon(newConfig.principal);
  }

  // Trocar logo quando o tema mudar (claro/escuro)
  switchLogoForTheme(isDark: boolean): void {
    const currentConfig = this.getLogoConfig();
    
    // Se temos ambas as logos (claro e escuro), trocar
    if (currentConfig.principal && currentConfig.secundario) {
      const newConfig: LogoConfig = {
        // Inverter: principal vira secundário e vice-versa
        principal: isDark ? currentConfig.secundario : currentConfig.principal,
        secundario: isDark ? currentConfig.principal : currentConfig.secundario,
        config: currentConfig.config,
        lastUpdated: new Date().toISOString(),
        version: currentConfig.version + 1
      };
      
      this.saveLogoConfig(newConfig);
      this.updateFavicon(newConfig.principal);
      console.log(`[LOGO] Tema alterado para ${isDark ? 'escuro' : 'claro'}`);
    }
  }

  // Método legado para compatibilidade
  async updateLogo(newLogoData: string): Promise<void> {
    await this.updateLogos({ principal: newLogoData });
  }

  // Obter URL do logo para uma área específica
  getLogoForArea(area: LogoArea): string {
    const config = this.getLogoConfig();
    const logoType = config.config[area] || 'principal';
    return logoType === 'secundario' ? config.secundario : config.principal;
  }

  // Obter URL do logo principal (legado)
  getCurrentLogoUrl(): string {
    return this.getLogoConfig().principal;
  }

  // Obter URL do logo secundário
  getSecondaryLogoUrl(): string {
    return this.getLogoConfig().secundario;
  }

  // Obter configuração de áreas
  getAreaConfig(): LogoAreaConfig {
    return this.getLogoConfig().config;
  }

  // Verificar se tem logo válido para uma área
  hasValidLogoForArea(area: LogoArea): boolean {
    const logoUrl = this.getLogoForArea(area);
    return Boolean(logoUrl && logoUrl !== '/logo.png' && logoUrl.length > 20);
  }

  // Notificar mudança
  private notifyLogoChange(config: LogoConfig): void {
    const event = new CustomEvent('logoChanged', { detail: config });
    window.dispatchEvent(event);
  }

  // Atualizar favicon
  private updateFavicon(logoData: string): void {
    try {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = logoData;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = logoData;
        document.head.appendChild(newFavicon);
      }
    } catch (error) {
      console.error('Erro ao atualizar favicon:', error);
    }
  }

  // Restaurar logo anterior
  restoreLogo(): void {
    const currentConfig = this.getLogoConfig();
    // Troca principal com secundário como backup
    const restoredConfig: LogoConfig = {
      principal: currentConfig.secundario || currentConfig.principal,
      secundario: currentConfig.principal,
      config: currentConfig.config,
      lastUpdated: new Date().toISOString(),
      version: currentConfig.version + 1
    };
    this.saveLogoConfig(restoredConfig);
  }

  // Obter histórico de alterações
  getLogoHistory(): Array<{ version: number; date: string; current: boolean }> {
    const config = this.getLogoConfig();
    return [
      {
        version: config.version,
        date: config.lastUpdated,
        current: true
      }
    ];
  }

  // Validar arquivo de imagem
  validateImageFile(file: File): { valid: boolean; error?: string } {
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Arquivo deve ser uma imagem' };
    }
    if (file.size > 2 * 1024 * 1024) {
      return { valid: false, error: 'Imagem deve ter no máximo 2MB' };
    }
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      return { valid: false, error: 'Formato não suportado. Use PNG, JPG, SVG ou WebP' };
    }
    return { valid: true };
  }

  // Converter arquivo para base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

export const logoService = LogoService.getInstance();
